/**
 * AllETFsPage — every US-listed ETF (~5,288 funds) in a single sortable /
 * searchable table.  Universe is scraped from stockanalysis.com/etf via
 * `/api/all-etfs/data`; the same endpoint kicks off a background refresh
 * that fetches Yahoo v8 chart with `events=div` per ETF so we can compute
 * returns + dividend metrics in a single round-trip.
 *
 * Visual language mirrors AllStocksPage:
 *   • black surface, white body text
 *   • light-blue (sky-300) ticker hyperlinks
 *   • green % gains, red % losses
 *   • sortable column headers with chevron indicator on the active column
 *   • industry/asset-class filter, search box, pagination
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Search, RefreshCw, ChevronDown, ChevronUp, ExternalLink,
  ChevronLeft, ChevronRight, Wallet, Download,
} from "lucide-react";
import { API } from "../config/api";
import AIAnalysisChat from "../components/AIAnalysisChat";

const PAGE_SIZE_OPTIONS = [50, 100, 200, 500];
const RETURN_COLUMNS = [
  { key: "ret_1d",   label: "1D"   },
  { key: "ret_5d",   label: "5D"   },
  { key: "ret_10d",  label: "10D"  },
  { key: "ret_20d",  label: "20D"  },
  { key: "ret_50d",  label: "50D"  },
  { key: "ret_100d", label: "100D" },
  { key: "ret_200d", label: "200D" },
  { key: "ret_1y",   label: "1Y"   },
];

const fmtPct = (v) => (v == null ? "—" : `${v >= 0 ? "+" : ""}${Number(v).toFixed(2)}%`);
const pctClass = (v) =>
  v == null ? "text-gray-500"
  : v >= 0  ? "text-green-400"
            : "text-red-400";
const fmtPrice = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);
const fmtAum = (v) => {
  if (v == null) return "—";
  if (v >= 1e12) return `$${(v / 1e12).toFixed(2)}T`;
  if (v >= 1e9)  return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6)  return `$${(v / 1e6).toFixed(2)}M`;
  return `$${v.toFixed(0)}`;
};
const fmtDiv = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);
const fmtYield = (v) => (v == null ? "—" : `${Number(v).toFixed(2)}%`);

export default function AllETFsPage() {
  const [rows, setRows] = useState([]);
  const [refreshState, setRefreshState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [assetClassFilter, setAssetClassFilter] = useState("");
  // Multi-sort: array of {key, dir} up to 7 entries. First is primary,
  // rest are tie-breakers.  Shift-click a header to add a secondary sort.
  const [sortSpecs, setSortSpecs] = useState([{ key: "ret_1d", dir: "desc" }]);
  const MAX_SORTS = 7;
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/all-etfs/data`, { timeout: 90000 });
      setRows(r.data?.rows || []);
      setRefreshState(r.data?.refresh_state || null);
    } catch (e) {
      setError(e?.message || "Failed to load.");
    } finally {
      setLoading(false);
    }
  }, []);

  const pollStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/all-etfs/status`, { timeout: 8000 });
      setRefreshState(r.data || null);
    } catch (_e) { /* swallow — transient */ }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // While background refresh is running, poll status every 4s and re-fetch
  // merged data every 12s so the table progressively fills.
  useEffect(() => {
    if (!refreshState?.running) return undefined;
    let cycles = 0;
    const id = setInterval(async () => {
      cycles += 1;
      await pollStatus();
      if (cycles % 3 === 0) {
        await loadData();
      }
    }, 4000);
    return () => clearInterval(id);
  }, [refreshState?.running, pollStatus, loadData]);

  const assetClasses = useMemo(() => {
    const s = new Set();
    rows.forEach(r => { if (r.asset_class) s.add(r.asset_class); });
    return Array.from(s).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toUpperCase();
    return rows.filter(r => {
      if (assetClassFilter && r.asset_class !== assetClassFilter) return false;
      if (!q) return true;
      return (
        (r.symbol || "").toUpperCase().includes(q) ||
        (r.name || "").toUpperCase().includes(q)
      );
    });
  }, [rows, search, assetClassFilter]);

  // Multi-sort — walk sortSpecs in order, first non-zero comparison wins.
  const sorted = useMemo(() => {
    const arr = [...filtered];
    const cmpOne = (a, b, key, dir) => {
      const av = a[key]; const bv = b[key];
      const aNull = av == null; const bNull = bv == null;
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      if (typeof av === "string" && typeof bv === "string") {
        return dir === "desc" ? bv.localeCompare(av) : av.localeCompare(bv);
      }
      return dir === "desc" ? bv - av : av - bv;
    };
    arr.sort((a, b) => {
      for (const s of sortSpecs) {
        const c = cmpOne(a, b, s.key, s.dir);
        if (c !== 0) return c;
      }
      return 0;
    });
    return arr;
  }, [filtered, sortSpecs]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => sorted.slice((safePage - 1) * pageSize, safePage * pageSize),
    [sorted, safePage, pageSize]
  );

  // Header click:
  //  • plain click primary  → cycle desc → asc → remove
  //  • plain click new key  → replace with [{key, desc}]
  //  • shift+click          → add or update as extra sort (up to 7)
  const onHeaderClick = (key, ev) => {
    const shift = ev?.shiftKey;
    setSortSpecs(prev => {
      const idx = prev.findIndex(s => s.key === key);
      if (shift) {
        if (idx < 0) {
          if (prev.length >= MAX_SORTS) return prev;
          return [...prev, { key, dir: "desc" }];
        }
        const nextDir = prev[idx].dir === "desc" ? "asc" : null;
        if (nextDir === null) return prev.filter(s => s.key !== key);
        return prev.map(s => s.key === key ? { ...s, dir: nextDir } : s);
      }
      if (idx === 0) {
        const nextDir = prev[0].dir === "desc" ? "asc" : null;
        if (nextDir === null) return prev.slice(1);
        return [{ key, dir: nextDir }, ...prev.slice(1)];
      }
      return [{ key, dir: "desc" }];
    });
    setPage(1);
  };
  const sortByKey = useMemo(() => {
    const m = {};
    sortSpecs.forEach((s, i) => { m[s.key] = { dir: s.dir, priority: i + 1 }; });
    return m;
  }, [sortSpecs]);

  const progressPct = refreshState && refreshState.total > 0
    ? Math.min(100, Math.round((refreshState.loaded / refreshState.total) * 100))
    : 100;

  // ── Export the FULL ETF universe (all rows, all fields).
  //
  // Direct top-level navigation via `window.location.assign(url)` is
  // the most reliable trigger.  The backend responds with
  // Content-Disposition: attachment so the current page stays put and
  // the browser saves the file — no popup, no iframe sandbox, no
  // SmartScreen quirks with Blob/data URLs.
  //
  // Both .xlsx and .csv are offered — CSV never gets blocked and
  // opens directly in Excel.
  const exportXlsxHref = `${API}/all-etfs/export.xlsx`;
  const exportCsvHref  = `${API}/all-etfs/export.csv`;
  const doDownload = useCallback((url) => {
    window.location.assign(url);
  }, []);

  // ── Compact ETFs-universe snapshot for the AI chat's context.
  //    top-N by each timeframe + breakouts + deteriorating + recovering.
  const universeSnapshot = useMemo(() => {
    if (!rows || rows.length === 0) return null;
    const topBy = (key, n = 8) => rows
      .filter(r => r[key] != null && r.aum != null && r.aum > 1e8)
      .sort((a, b) => (b[key] ?? 0) - (a[key] ?? 0))
      .slice(0, n)
      .map(r => ({ sym: r.symbol, name: r.name, val: r[key], aum: r.aum }));
    const bottomBy = (key, n = 8) => rows
      .filter(r => r[key] != null && r.aum != null && r.aum > 1e8)
      .sort((a, b) => (a[key] ?? 0) - (b[key] ?? 0))
      .slice(0, n)
      .map(r => ({ sym: r.symbol, name: r.name, val: r[key], aum: r.aum }));
    const breakouts = rows
      .filter(r => r.aum > 5e8 && r.ret_1d > 0 && r.ret_5d > 0 && r.ret_20d > 5 && r.ret_50d > 10)
      .sort((a, b) => (b.ret_50d ?? 0) - (a.ret_50d ?? 0))
      .slice(0, 10)
      .map(r => ({ sym: r.symbol, name: r.name, r5d: r.ret_5d, r20d: r.ret_20d, r50d: r.ret_50d }));
    const deteriorating = rows
      .filter(r => r.aum > 5e8 && r.ret_1d < 0 && r.ret_5d < 0 && r.ret_20d < 0 && r.ret_50d < 0 && r.ret_200d < 0)
      .sort((a, b) => (a.ret_50d ?? 0) - (b.ret_50d ?? 0))
      .slice(0, 10)
      .map(r => ({ sym: r.symbol, name: r.name, r20d: r.ret_20d, r50d: r.ret_50d, r200d: r.ret_200d }));
    const recovering = rows
      .filter(r => r.aum > 5e8 && r.ret_20d > 6 && r.ret_200d != null && r.ret_200d < -8)
      .sort((a, b) => (b.ret_20d ?? 0) - (a.ret_20d ?? 0))
      .slice(0, 10)
      .map(r => ({ sym: r.symbol, name: r.name, r20d: r.ret_20d, r200d: r.ret_200d }));
    return {
      universe_size: rows.length,
      top_1d:   topBy("ret_1d"),
      top_5d:   topBy("ret_5d"),
      top_20d:  topBy("ret_20d"),
      top_50d:  topBy("ret_50d"),
      top_1y:   topBy("ret_1y"),
      worst_1d: bottomBy("ret_1d"),
      worst_1y: bottomBy("ret_1y"),
      breakouts,
      deteriorating,
      recovering,
    };
  }, [rows]);

  return (
    <div className="min-h-screen bg-black text-white p-2" data-testid="all-etfs-page">
      {/* AI Chat — moved to top of page */}
      <div className="mb-2">
        <AIAnalysisChat
          mode="universe-etfs"
          extraContext={universeSnapshot || { universe_size: rows?.length ?? 0 }}
        />
      </div>
      <div className="bg-[#111] border border-gray-800 rounded">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1a1a2e] to-[#111] px-4 py-3 border-b border-gray-800 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Wallet className="w-4 h-4 text-emerald-400" />
            <span className="text-sm font-semibold text-white">All ETFs</span>
            <span className="text-[10px] text-gray-500 ml-2">
              {rows.length.toLocaleString()} funds · source: stockanalysis.com
            </span>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {refreshState?.running ? (
              <div className="flex items-center gap-2 text-[10px] text-gray-300" data-testid="all-etfs-progress">
                <RefreshCw className="w-3 h-3 animate-spin text-emerald-400" />
                <span className="font-mono">
                  Loading metrics {refreshState.loaded}/{refreshState.total} ({progressPct}%)
                </span>
                <div className="w-24 h-1.5 bg-gray-800 rounded overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 transition-all"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              </div>
            ) : (
              <span className="text-[10px] text-gray-500 font-mono">
                {refreshState?.loaded || 0} of {rows.length || refreshState?.total || 0} ETFs cached
              </span>
            )}
            <div className="inline-flex rounded overflow-hidden border border-emerald-700" data-testid="all-etfs-export-group">
              <button
                type="button"
                onClick={() => doDownload(exportXlsxHref)}
                disabled={rows.length === 0}
                className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 bg-emerald-800/40 text-emerald-100 hover:bg-emerald-700/60 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="all-etfs-export"
                title={`Download all ${rows.length.toLocaleString()} ETFs as Excel (.xlsx)`}
              >
                <Download className="w-3 h-3" />
                Excel
              </button>
              <button
                type="button"
                onClick={() => doDownload(exportCsvHref)}
                disabled={rows.length === 0}
                className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 bg-emerald-900/40 text-emerald-100 hover:bg-emerald-700/60 disabled:opacity-40 disabled:cursor-not-allowed border-l border-emerald-700"
                data-testid="all-etfs-export-csv"
                title={`Download all ${rows.length.toLocaleString()} ETFs as CSV (opens in Excel)`}
              >
                CSV
              </button>
            </div>
            <button
              onClick={loadData}
              disabled={loading}
              className="text-gray-400 hover:text-white p-1 rounded hover:bg-gray-800"
              data-testid="all-etfs-refresh"
              title="Refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* Controls */}
        <div className="px-4 py-2 border-b border-gray-800 flex items-center gap-3 flex-wrap bg-[#0d0d0d]">
          <div className="flex items-center gap-1.5 bg-black border border-gray-700 rounded px-2 py-1 flex-1 min-w-[220px] max-w-[420px]">
            <Search className="w-3 h-3 text-gray-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search symbol or ETF name..."
              className="bg-transparent outline-none text-[11px] text-white placeholder-gray-600 w-full"
              data-testid="all-etfs-search"
            />
          </div>
          <select
            value={assetClassFilter}
            onChange={(e) => { setAssetClassFilter(e.target.value); setPage(1); }}
            className="bg-black border border-gray-700 text-white text-[11px] rounded px-2 py-1"
            data-testid="all-etfs-asset-filter"
          >
            <option value="">All asset classes ({assetClasses.length})</option>
            {assetClasses.map(ac => (
              <option key={ac} value={ac}>{ac}</option>
            ))}
          </select>
          <div className="flex items-center gap-1 ml-auto text-[10px] text-gray-400">
            Rows per page:
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              className="bg-black border border-gray-700 text-white text-[11px] rounded px-2 py-1"
              data-testid="all-etfs-page-size"
            >
              {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <span className="ml-2 font-mono">
              {sorted.length.toLocaleString()} matches · page {safePage}/{totalPages}
            </span>
          </div>
        </div>

        {/* Sort pills — shift+click adds secondary, up to 7 sorts */}
        <div className="px-4 py-2 border-b border-gray-800 flex items-center gap-2 flex-wrap bg-[#0d0d0d]">
          <span className="text-[10px] text-gray-500 uppercase tracking-wider mr-1">
            Sort by <span className="text-gray-600 normal-case tracking-normal ml-1">(shift+click for secondary, up to 7)</span>
          </span>
          {RETURN_COLUMNS.map(c => {
            const info = sortByKey[c.key];
            return (
              <button
                key={c.key}
                onClick={(e) => onHeaderClick(c.key, e)}
                data-testid={`all-etfs-sort-${c.key}`}
                className={`px-2 py-1 text-[10px] font-medium rounded-md transition-all flex items-center gap-1
                  ${info ? "bg-emerald-600 text-white" : "bg-gray-900 text-gray-400 hover:text-white"}`}
              >
                {c.label}
                {info && (info.dir === "desc" ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)}{info && sortSpecs.length > 1 && <span className="ml-0.5 text-[9px] font-mono bg-black/40 rounded px-1 leading-none py-[1px]">{info.priority}</span>}
              </button>
            );
          })}
          <button
            onClick={(e) => onHeaderClick("div_yield", e)}
            className={`px-2 py-1 text-[10px] font-medium rounded-md transition-all flex items-center gap-1
              ${sortByKey["div_yield"] ? "bg-emerald-600 text-white" : "bg-gray-900 text-gray-400 hover:text-white"}`}
            data-testid="all-etfs-sort-div_yield"
          >
            Div Yield
            {sortByKey["div_yield"] && <ChevronDown className="w-3 h-3" />}
          </button>
          <button
            onClick={(e) => onHeaderClick("aum", e)}
            className={`px-2 py-1 text-[10px] font-medium rounded-md transition-all flex items-center gap-1
              ${sortByKey["aum"] ? "bg-emerald-600 text-white" : "bg-gray-900 text-gray-400 hover:text-white"}`}
            data-testid="all-etfs-sort-aum"
          >
            AUM
            {sortByKey["aum"] && <ChevronDown className="w-3 h-3" />}
          </button>
          {sortSpecs.length > 1 && (
            <button
              onClick={() => { setSortSpecs([{ key: "ret_1d", dir: "desc" }]); setPage(1); }}
              data-testid="all-etfs-sort-reset"
              className="ml-2 px-2 py-1 text-[10px] font-medium rounded-md bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white transition-all"
            >
              Reset sorts
            </button>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-[11px] min-w-[1400px]">
            <thead>
              <tr className="text-[9px] text-gray-500 uppercase tracking-wider border-b border-gray-800 bg-[#0d0d0d]">
                <th className="px-3 py-2 text-left cursor-pointer hover:text-white" onClick={(e) => onHeaderClick("symbol", e)} data-testid="header-symbol">
                  <span className="inline-flex items-center gap-1">Symbol
                    {sortByKey["symbol"] && (sortByKey["symbol"].dir === "desc" ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)}{sortByKey["symbol"] && sortSpecs.length > 1 && <span className="ml-0.5 text-[8px] font-mono bg-emerald-800/70 rounded px-1">{sortByKey["symbol"].priority}</span>}
                  </span>
                </th>
                <th className="px-3 py-2 text-left cursor-pointer hover:text-white" onClick={(e) => onHeaderClick("name", e)} data-testid="header-name">
                  <span className="inline-flex items-center gap-1">Fund Name
                    {sortByKey["name"] && (sortByKey["name"].dir === "desc" ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)}{sortByKey["name"] && sortSpecs.length > 1 && <span className="ml-0.5 text-[8px] font-mono bg-emerald-800/70 rounded px-1">{sortByKey["name"].priority}</span>}
                  </span>
                </th>
                <th className="px-3 py-2 text-left cursor-pointer hover:text-white" onClick={(e) => onHeaderClick("asset_class", e)} data-testid="header-asset">
                  <span className="inline-flex items-center gap-1">Asset Class
                    {sortByKey["asset_class"] && (sortByKey["asset_class"].dir === "desc" ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)}{sortByKey["asset_class"] && sortSpecs.length > 1 && <span className="ml-0.5 text-[8px] font-mono bg-emerald-800/70 rounded px-1">{sortByKey["asset_class"].priority}</span>}
                  </span>
                </th>
                <th className="px-3 py-2 text-right cursor-pointer hover:text-white" onClick={(e) => onHeaderClick("aum", e)} data-testid="header-aum">
                  <span className="inline-flex items-center gap-1 justify-end w-full">AUM
                    {sortByKey["aum"] && (sortByKey["aum"].dir === "desc" ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)}{sortByKey["aum"] && sortSpecs.length > 1 && <span className="ml-0.5 text-[8px] font-mono bg-emerald-800/70 rounded px-1">{sortByKey["aum"].priority}</span>}
                  </span>
                </th>
                <th className="px-3 py-2 text-right cursor-pointer hover:text-white" onClick={(e) => onHeaderClick("price", e)} data-testid="header-price">
                  <span className="inline-flex items-center gap-1 justify-end w-full">Price
                    {sortByKey["price"] && (sortByKey["price"].dir === "desc" ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)}{sortByKey["price"] && sortSpecs.length > 1 && <span className="ml-0.5 text-[8px] font-mono bg-emerald-800/70 rounded px-1">{sortByKey["price"].priority}</span>}
                  </span>
                </th>
                <th className="px-3 py-2 text-right cursor-pointer hover:text-white" onClick={(e) => onHeaderClick("div_payout", e)} data-testid="header-div_payout">
                  <span className="inline-flex items-center gap-1 justify-end w-full">Div Payout (TTM)
                    {sortByKey["div_payout"] && (sortByKey["div_payout"].dir === "desc" ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)}{sortByKey["div_payout"] && sortSpecs.length > 1 && <span className="ml-0.5 text-[8px] font-mono bg-emerald-800/70 rounded px-1">{sortByKey["div_payout"].priority}</span>}
                  </span>
                </th>
                <th className={`px-3 py-2 text-right cursor-pointer hover:text-white ${sortByKey["div_yield"] ? "text-emerald-300 font-bold" : ""}`} onClick={(e) => onHeaderClick("div_yield", e)} data-testid="header-div_yield">
                  <span className="inline-flex items-center gap-1 justify-end w-full">Div Yield
                    {sortByKey["div_yield"] && (sortByKey["div_yield"].dir === "desc" ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)}{sortByKey["div_yield"] && sortSpecs.length > 1 && <span className="ml-0.5 text-[8px] font-mono bg-emerald-800/70 rounded px-1">{sortByKey["div_yield"].priority}</span>}
                  </span>
                </th>
                <th className="px-3 py-2 text-center cursor-pointer hover:text-white" onClick={(e) => onHeaderClick("div_frequency", e)} data-testid="header-div_frequency">
                  <span className="inline-flex items-center gap-1 justify-center w-full">Payout Freq.
                    {sortByKey["div_frequency"] && (sortByKey["div_frequency"].dir === "desc" ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)}{sortByKey["div_frequency"] && sortSpecs.length > 1 && <span className="ml-0.5 text-[8px] font-mono bg-emerald-800/70 rounded px-1">{sortByKey["div_frequency"].priority}</span>}
                  </span>
                </th>
                <th className="px-3 py-2 text-right cursor-pointer hover:text-white" onClick={(e) => onHeaderClick("payout_ratio", e)} data-testid="header-payout_ratio">
                  <span className="inline-flex items-center gap-1 justify-end w-full">Payout Ratio
                    {sortByKey["payout_ratio"] && (sortByKey["payout_ratio"].dir === "desc" ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)}{sortByKey["payout_ratio"] && sortSpecs.length > 1 && <span className="ml-0.5 text-[8px] font-mono bg-emerald-800/70 rounded px-1">{sortByKey["payout_ratio"].priority}</span>}
                  </span>
                </th>
                {RETURN_COLUMNS.map(c => (
                  <th
                    key={c.key}
                    className={`px-3 py-2 text-right cursor-pointer hover:text-white ${sortByKey[c.key] ? "text-emerald-300 font-bold" : ""}`}
                    onClick={(e) => onHeaderClick(c.key, e)}
                    data-testid={`header-${c.key}`}
                  >
                    <span className="inline-flex items-center gap-1 justify-end w-full">
                      {c.label}
                      {sortByKey[c.key] && (sortByKey[c.key].dir === "desc" ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)}{sortByKey[c.key] && sortSpecs.length > 1 && <span className="ml-0.5 text-[8px] font-mono bg-emerald-800/70 rounded px-1">{sortByKey[c.key].priority}</span>}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {error && (
                <tr>
                  <td colSpan={15} className="px-3 py-6 text-center text-red-400 text-xs">{error}</td>
                </tr>
              )}
              {!error && loading && rows.length === 0 && (
                <tr>
                  <td colSpan={15} className="px-3 py-12 text-center text-gray-400 text-xs">
                    <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2 text-emerald-500" />
                    Loading the full ETF universe…
                  </td>
                </tr>
              )}
              {!error && !loading && pageRows.length === 0 && rows.length > 0 && (
                <tr>
                  <td colSpan={15} className="px-3 py-6 text-center text-gray-500 text-xs">No matches.</td>
                </tr>
              )}
              {pageRows.map((r) => (
                <tr
                  key={r.symbol}
                  className="border-b border-gray-900 hover:bg-gray-900/40 transition-colors"
                  data-testid={`all-etfs-row-${r.symbol}`}
                >
                  <td className="px-3 py-1.5 font-mono font-bold">
                    <Link
                      to={`/stock?ticker=${encodeURIComponent(r.symbol)}`}
                      className="text-sky-300 hover:text-sky-200 hover:underline"
                      data-testid={`row-${r.symbol}-symbol`}
                    >
                      {r.symbol}
                    </Link>
                  </td>
                  <td className="px-3 py-1.5 text-white truncate max-w-[260px]" title={r.name || ""}>
                    {r.name || "—"}
                  </td>
                  <td className="px-3 py-1.5 text-white truncate max-w-[170px]" title={r.asset_class || ""}>
                    {r.asset_class || "—"}
                  </td>
                  <td className="px-3 py-1.5 text-right text-white font-mono">{fmtAum(r.aum)}</td>
                  <td className="px-3 py-1.5 text-right text-white font-mono">{fmtPrice(r.price)}</td>
                  <td className="px-3 py-1.5 text-right text-white font-mono">{fmtDiv(r.div_payout)}</td>
                  <td className={`px-3 py-1.5 text-right font-mono ${r.div_yield != null && r.div_yield > 0 ? "text-emerald-300" : "text-gray-500"}`}>
                    {fmtYield(r.div_yield)}
                  </td>
                  <td className="px-3 py-1.5 text-center text-white text-[10px]">
                    {r.div_frequency || "—"}
                  </td>
                  <td className="px-3 py-1.5 text-right text-white font-mono">
                    {r.payout_ratio == null ? "—" : `${Number(r.payout_ratio).toFixed(1)}%`}
                  </td>
                  {RETURN_COLUMNS.map(c => (
                    <td
                      key={c.key}
                      className={`px-3 py-1.5 text-right font-mono ${pctClass(r[c.key])}`}
                      data-testid={`row-${r.symbol}-${c.key}`}
                    >
                      {fmtPct(r[c.key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination footer */}
        <div className="px-4 py-2 border-t border-gray-800 flex items-center justify-between bg-[#0d0d0d]">
          <div className="text-[10px] text-gray-500 font-mono">
            Showing {pageRows.length ? (safePage - 1) * pageSize + 1 : 0}–{(safePage - 1) * pageSize + pageRows.length} of {sorted.length.toLocaleString()}
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(1)} disabled={safePage <= 1}
              className="text-[10px] px-2 py-1 rounded border border-gray-700 text-gray-300 hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="page-first">First</button>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage <= 1}
              className="text-[10px] p-1 rounded border border-gray-700 text-gray-300 hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="page-prev"><ChevronLeft className="w-3 h-3" /></button>
            <span className="text-[10px] text-gray-300 font-mono px-2">{safePage} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}
              className="text-[10px] p-1 rounded border border-gray-700 text-gray-300 hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="page-next"><ChevronRight className="w-3 h-3" /></button>
            <button onClick={() => setPage(totalPages)} disabled={safePage >= totalPages}
              className="text-[10px] px-2 py-1 rounded border border-gray-700 text-gray-300 hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="page-last">Last</button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-gray-800 text-[9px] text-gray-600 flex items-center justify-between bg-[#0d0d0d]">
          <span className="inline-flex items-center gap-1">
            <ExternalLink className="w-3 h-3" />
            Data: stockanalysis.com (universe) · Yahoo Finance (returns + dividend events)
          </span>
          <span>Refresh state: {refreshState?.running ? "filling cache…" : "idle"}</span>
        </div>
      </div>
    </div>
  );
}
